// Apolo IDE — FileTree (Electron-only filesystem tree).
//
// Uses window.apolo.file.list (via preload IPC) to lazy-load directory
// contents on demand. When the Electron bridge isn't present (browser
// preview, tests), shows a stub message.

import { useCallback, useEffect, useState } from 'react';
import { ChevronRight, File, Folder, FolderOpen } from 'lucide-react';
import { ELECTRON_REQUIRED_MESSAGE, hasElectronBridge } from '../../lib/electronBridge.js';

function FileEntry({ entry, depth, root, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState(null);
  const [loading, setLoading] = useState(false);

  async function toggle() {
    if (!entry.isDirectory) {
      onOpen?.(entry);
      return;
    }
    if (expanded) {
      setExpanded(false);
      return;
    }
    if (children === null) {
      setLoading(true);
      try {
        const list = await window.apolo.file.list({ root, dir: entry.path });
        setChildren(list);
      } catch (e) {
        console.error('[apolo:file:list]', e);
        setChildren([]);
      } finally {
        setLoading(false);
      }
    }
    setExpanded(true);
  }

  const Icon = entry.isDirectory ? (expanded ? FolderOpen : Folder) : File;

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        style={{ paddingLeft: `${8 + depth * 12}px` }}
        className="flex w-full items-center gap-1 rounded-md px-2 py-1 text-left text-sm text-apolo-textSecondary transition hover:bg-apolo-surface2/60 focus:outline-none focus:ring-1 focus:ring-apolo-accent/50"
      >
        {entry.isDirectory ? (
          <ChevronRight
            size={10}
            className={`shrink-0 text-apolo-textTertiary transition ${expanded ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
        ) : (
          <span className="inline-block w-[10px]" aria-hidden="true" />
        )}
        <Icon size={11} className="shrink-0 text-apolo-textTertiary" aria-hidden="true" />
        <span className="truncate">{entry.name}</span>
      </button>
      {expanded && (
        <ul role="group">
          {loading && (
            <li className="px-3 py-1 text-xs text-apolo-textTertiary" style={{ paddingLeft: `${20 + depth * 12}px` }}>
              carregando…
            </li>
          )}
          {!loading &&
            children?.map((child) => (
              <li key={child.path} role="treeitem">
                <FileEntry entry={child} depth={depth + 1} root={root} onOpen={onOpen} />
              </li>
            ))}
          {!loading && children?.length === 0 && (
            <li className="px-3 py-1 text-xs text-apolo-textTertiary" style={{ paddingLeft: `${20 + depth * 12}px` }}>
              (vazio)
            </li>
          )}
        </ul>
      )}
    </>
  );
}

export default function FileTree({ root, onOpenFile }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!hasElectronBridge()) {
      setError(ELECTRON_REQUIRED_MESSAGE);
      return;
    }
    if (!root) {
      setError('Selecione um diretório para a sessão antes de navegar o FileTree.');
      return;
    }
    try {
      setError(null);
      const list = await window.apolo.file.list({ root, dir: '' });
      setEntries(list);
    } catch (e) {
      setError(e?.message || 'Falha ao listar diretório');
    }
  }, [root]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center px-4 py-6 text-center">
        <p className="max-w-xs text-xs text-apolo-textTertiary">{error}</p>
      </div>
    );
  }
  if (!entries) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-apolo-textTertiary">
        carregando…
      </div>
    );
  }

  return (
    <nav aria-label="Árvore de arquivos" className="flex flex-col gap-0.5 py-2">
      <ul role="tree">
        {entries.map((entry) => (
          <li key={entry.path} role="treeitem">
            <FileEntry entry={entry} depth={0} root={root} onOpen={onOpenFile} />
          </li>
        ))}
      </ul>
    </nav>
  );
}
